import {SubNav} from '@primer/react-brand'
import {forwardRef, useEffect, useState} from 'react'

import {isLink} from '../../../schemas/contentful/contentTypes/link'
import type {PrimerComponentSubnav} from '../../../schemas/contentful/contentTypes/primerComponentSubnav'
import {isCurrentLink} from './utils'
import {getAnalyticsEvent} from '../../../lib/utils/analytics'

export type ContentfulSubnavProps = {
  component: PrimerComponentSubnav

  hasShadow?: boolean
  className?: string
  linkVariant?: 'default' | 'muted'
  'data-color-mode'?: string
}

const ANALYTICS_LOCATION = 'subnav'

export const ContentfulSubnav = forwardRef<HTMLDivElement, ContentfulSubnavProps>(({component, ...props}, ref) => {
  const [actualUrl, setActualUrl] = useState<string | undefined>(undefined)

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setActualUrl(window.location.href)
  }, [])

  return (
    <div data-color-mode={props['data-color-mode']}>
      <SubNav className={props.className} hasShadow={props.hasShadow} ref={ref}>
        <SubNav.Heading
          data-ref={`subnav-heading-link-${component.fields.heading.sys.id}`}
          href={component.fields.heading.fields.href}
          {...getAnalyticsEvent({
            action: component.fields.heading.fields.text,
            tag: 'link',
            location: ANALYTICS_LOCATION,
          })}
        >
          {component.fields.heading.fields.text}
        </SubNav.Heading>

        {component.fields.subheading && (
          <SubNav.SubHeading
            data-ref={`subnav-subheading-link-${component.fields.subheading.sys.id}`}
            href={component.fields.subheading.fields.href}
            {...(isCurrentLink({url: component.fields.subheading.fields.href, actual: actualUrl})
              ? {'aria-current': 'page'}
              : {})}
            {...getAnalyticsEvent({
              action: component.fields.subheading.fields.text,
              tag: 'link',
              location: ANALYTICS_LOCATION,
            })}
          >
            {component.fields.subheading.fields.text}
          </SubNav.SubHeading>
        )}

        {component.fields.links.map(linkOrLinkGroup => {
          if (isLink(linkOrLinkGroup)) {
            const {href, text} = linkOrLinkGroup.fields

            const isCurrent = isCurrentLink({url: href, actual: actualUrl})

            return (
              <SubNav.Link
                data-ref={`subnav-link-${linkOrLinkGroup.sys.id}`}
                key={linkOrLinkGroup.sys.id}
                href={href}
                variant={props.linkVariant}
                {...(isCurrent ? {'aria-current': 'page'} : {})}
                {...getAnalyticsEvent({
                  action: text,
                  tag: 'link',
                  location: ANALYTICS_LOCATION,
                })}
              >
                {text}
              </SubNav.Link>
            )
          }

          /**
           * If we're here, it means we have a link group.
           */
          const {heading, links = []} = linkOrLinkGroup.fields

          return (
            <SubNav.Link
              key={heading.sys.id}
              href={heading.fields.href}
              variant={props.linkVariant}
              {...(isCurrentLink({url: heading.fields.href, actual: actualUrl}) ? {'aria-current': 'page'} : {})}
            >
              {heading.fields.text}

              {links.length > 0 && (
                <SubNav.SubMenu>
                  {links.map(link => (
                    <SubNav.Link
                      key={link.sys.id}
                      href={link.fields.href}
                      variant={props.linkVariant}
                      data-ref={`subnav-submenu-link-${link.sys.id}`}
                      {...(isCurrentLink({url: link.fields.href, actual: actualUrl}) ? {'aria-current': 'page'} : {})}
                      {...getAnalyticsEvent({
                        action: link.fields.text,
                        tag: 'link',
                        context: heading.fields.text,
                        location: ANALYTICS_LOCATION,
                      })}
                    >
                      {link.fields.text}
                    </SubNav.Link>
                  ))}
                </SubNav.SubMenu>
              )}
            </SubNav.Link>
          )
        })}

        {component.fields.cta !== undefined && (
          <SubNav.Action
            href={component.fields.cta.fields.href}
            data-ref={`subnav-cta-action-${component.fields.cta.sys.id}`}
          >
            {component.fields.cta.fields.text}
          </SubNav.Action>
        )}
      </SubNav>
    </div>
  )
})

ContentfulSubnav.displayName = 'ContentfulSubnav'
